/**
 * tutorial/index.js — first-run onboarding (GDD §4 Tutorial, §2.4 first-time
 * VR users).
 *
 * Six steps that walk a new player from the entrance gate to their first
 * payout, then point them at the Prize Desk. It runs once: when the save says
 * `tutorial.completed` is false and `?skiptutorial=1` was not passed. Test mode
 * skips it by default, which is why the whole suite is unaffected by its
 * existence.
 *
 * Three rules shaped this module, and breaking any of them would make it worse
 * than nothing:
 *
 *   1. **It never locks input.** There is no modal, no pause, no "press next".
 *      The card is `pointer-events: none` apart from its own two buttons, so a
 *      player who ignores the tutorial entirely can still play the whole game.
 *   2. **Every step advances on the real event** — STALL_APPROACH, ACTIVATE,
 *      THROW, ROUND_END — never on a timer pretending to be progress. A step
 *      the player has already satisfied by accident is a step they never see.
 *   3. **It is always skippable**, from the card's own button or the Esc menu,
 *      and the steps that cannot be completed by an event carry a timeout so
 *      onboarding can never become a dead end.
 *
 * Two presentations of one state machine, matching `naming-ui.js`: a DOM card
 * on desktop and a camera-parented panel in VR, because a DOM overlay is
 * invisible in a headset.
 *
 * @module tutorial
 */

import * as THREE from 'three';
import { EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { save } from '../core/save.js';
import { teleport } from '../core/teleport.js';
import { options } from '../core/testmode.js';
import { createPanel } from '../ui/panels.js';
import { TUTORIAL_STEPS, TUTORIAL_STALL } from './steps.js';

const DOM_ID = 'cc-tutorial';
/** How far the player must waddle for the welcome step to count as answered. */
const MOVED_DISTANCE = 2.2;

/** Teleport nodes the floating marker can sit over, by step `marker` key. */
const MARKER_NODES = {
  spawn: 'hub-entrance',
  stall: `stall-${TUTORIAL_STALL}`,
  'prize-desk': 'prize-counter',
  portal: 'personal-space',
};

class Tutorial {
  constructor() {
    /** @type {THREE.Scene} */ this.scene = null;
    /** @type {THREE.Camera} */ this.camera = null;
    /** @type {object} */ this.avatar = null;

    this.active = false;
    this.completed = false;
    this.skipped = false;
    this.index = -1;

    /** @type {Array<() => void>} Bus unsubscribes for the live step. */
    this._offs = [];
    /** @type {THREE.Group} */ this._marker = null;
    /** @type {THREE.Mesh} */ this._panel = null;
    /** @type {Record<string, HTMLElement>} */ this._dom = {};
    this._menuOpen = false;
    this._elapsed = 0;
    this._stepTime = 0;
    this._startPos = new THREE.Vector3();
    this._inVR = false;
  }

  /**
   * @param {{ scene: THREE.Scene, camera: THREE.Camera, avatar: object }} refs
   */
  init({ scene, camera, avatar }) {
    this.scene = scene;
    this.camera = camera;
    this.avatar = avatar;
    this.completed = !!save.data?.tutorial?.completed;

    this._buildMarker();
    this._buildPanel();
    this._buildDom();

    bus.on(EVENTS.XR_SESSION_START, () => this._setVR(true));
    bus.on(EVENTS.XR_SESSION_END, () => this._setVR(false));

    // Esc opens the little menu, but only when it cannot be mistaken for
    // "leave the stall" — main.js gives that meaning to Esc whenever a stall
    // is live, and stealing it would strand the player inside a round.
    bus.on(EVENTS.INPUT_ACTION, ({ action }) => {
      if (action !== 'exit' || !this.active) return;
      if (this._stallActive) return;
      this._toggleMenu();
    });
    bus.on(EVENTS.STALL_ACTIVATE, () => this._setInStall(true));
    bus.on(EVENTS.STALL_EXIT, () => this._setInStall(false));

    return this;
  }

  /**
   * Begin onboarding at step one. No-op if it has already been completed, or
   * if it is already running.
   * @returns {boolean} Whether it started.
   */
  start() {
    if (this.active || this.completed) return false;
    this.active = true;
    this.skipped = false;
    this.index = -1;
    this._startPos.copy(this._playerPosition());
    this._goto(0);
    return true;
  }

  /**
   * Abandon onboarding. Marks it done so it never reappears — a player who
   * skipped it does not want it again on the next launch.
   * @returns {boolean}
   */
  skip() {
    if (!this.active) return false;
    this.skipped = true;
    this._finish(false);
    return true;
  }

  /**
   * Force the current step to complete, as if the player had performed it.
   * The automation surface uses this to walk the flow without physics.
   * @returns {boolean}
   */
  advance() {
    if (!this.active) return false;
    this._next();
    return true;
  }

  /** @returns {object} A snapshot for `window.__cc.tutorial.state()`. */
  state() {
    const step = TUTORIAL_STEPS[this.index] || null;
    return {
      active: this.active,
      completed: this.completed,
      skipped: this.skipped,
      index: this.index,
      total: TUTORIAL_STEPS.length,
      stepId: step ? step.id : null,
      title: step ? step.title : null,
      body: step ? step.body : null,
      marker: step ? step.marker : null,
      markerVisible: !!this._marker?.visible,
      menuOpen: this._menuOpen,
      panelVisible: !!this._panel?.visible,
    };
  }

  /** @returns {string[]} Step ids in order. */
  steps() {
    return TUTORIAL_STEPS.map((s) => s.id);
  }

  /** @param {number} dt */
  update(dt) {
    this._elapsed += dt;
    if (this._marker && this._marker.visible) {
      // A marker that only bobs reads as decoration; one that also spins reads
      // as "look at me".
      this._marker.position.y = this._markerY + Math.sin(this._elapsed * 2.4) * 0.16;
      this._marker.rotation.y += dt * 1.1;
    }
    if (!this.active) return;

    this._stepTime += dt;
    const step = TUTORIAL_STEPS[this.index];
    if (!step) return;

    if (step.gesture === 'moved'
      && this._playerPosition().distanceTo(this._startPos) > MOVED_DISTANCE) {
      this._next();
      return;
    }
    if (step.timeout && this._stepTime >= step.timeout) this._next();
  }

  // ── Step machine ──────────────────────────────────────────────────────────

  /** @param {number} i @private */
  _goto(i) {
    this._clearStepListeners();
    this.index = i;
    this._stepTime = 0;

    const step = TUTORIAL_STEPS[i];
    if (!step) { this._finish(true); return; }

    if (step.event) {
      this._offs.push(bus.on(step.event, (payload) => {
        if (step.when && !step.when(payload || {})) return;
        // Deferred: the emitting module is mid-handler, and swapping the card
        // out from under it has surprised more than one listener.
        setTimeout(() => { if (TUTORIAL_STEPS[this.index] === step) this._next(); }, 0);
      }));
    }

    this._render(step);
    this._placeMarker(step.marker);
  }

  /** @private */
  _next() {
    if (!this.active) return;
    this._goto(this.index + 1);
  }

  /**
   * @param {boolean} earned True when the player reached the end rather than
   *   skipping, which is the only case that gets the fanfare.
   * @private
   */
  _finish(earned) {
    this._clearStepListeners();
    this.active = false;
    this.completed = true;
    this.index = -1;
    this._menuOpen = false;

    if (save.data?.tutorial) {
      save.data.tutorial.completed = true;
      save.markDirty();
    }

    this._hideDom();
    this._placeMarker(null);
    if (this._panel) this._panel.visible = false;

    if (!earned) {
      bus.emit(EVENTS.UI_TOAST, { text: 'Tutorial skipped — honk freely', kind: 'good' });
      return;
    }

    bus.emit(EVENTS.UI_TOAST, { text: 'You know the midway now. HONK!', kind: 'prize', duration: 3600 });
    // Three honks is a fanfare; one is a goose.
    for (let i = 0; i < 3; i++) {
      setTimeout(() => bus.emit(EVENTS.AUDIO_PLAY, { name: 'honk' }), i * 190);
    }
  }

  /** @private */
  _clearStepListeners() {
    for (const off of this._offs) off();
    this._offs.length = 0;
  }

  /** @returns {THREE.Vector3} @private */
  _playerPosition() {
    return this.avatar?.getPosition ? this.avatar.getPosition() : new THREE.Vector3();
  }

  // ── The floating marker ───────────────────────────────────────────────────

  /** @private */
  _buildMarker() {
    const g = new THREE.Group();
    g.name = 'tutorial-marker';
    g.visible = false;

    const gold = 0xffd166;
    const arrow = new THREE.Mesh(
      new THREE.ConeGeometry(0.34, 0.62, 4),
      new THREE.MeshBasicMaterial({ color: gold, transparent: true, opacity: 0.92 }),
    );
    arrow.rotation.x = Math.PI;      // point at the ground
    arrow.rotation.y = Math.PI / 4;  // read as a diamond, not a flat triangle
    g.add(arrow);

    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.1, 3.2, 10, 1, true),
      new THREE.MeshBasicMaterial({
        color: gold, transparent: true, opacity: 0.14,
        side: THREE.DoubleSide, depthWrite: false,
      }),
    );
    beam.position.y = -1.9;
    g.add(beam);

    this._marker = g;
    this._markerY = 3.1;
    this.scene.add(g);

    // The ring sits on the ground and does not bob with the arrow, so it needs
    // to be its own object rather than a child of the marker group.
    this._ring = new THREE.Mesh(
      new THREE.RingGeometry(0.62, 0.86, 32),
      new THREE.MeshBasicMaterial({
        color: gold, transparent: true, opacity: 0.55,
        side: THREE.DoubleSide, depthWrite: false,
      }),
    );
    this._ring.rotation.x = -Math.PI / 2;
    this._ring.position.y = 0.03;
    this._ring.visible = false;
    this.scene.add(this._ring);
  }

  /**
   * @param {string|null} key A `MARKER_NODES` key, or null to hide.
   * @private
   */
  _placeMarker(key) {
    const nodeId = key ? MARKER_NODES[key] : null;
    const node = nodeId ? teleport.getNode(nodeId) : null;
    const on = !!node;

    this._marker.visible = on;
    this._ring.visible = on;
    if (!on) return;

    this._marker.position.set(node.position.x, this._markerY, node.position.z);
    this._ring.position.set(node.position.x, 0.03, node.position.z);
  }

  // ── Presentation ──────────────────────────────────────────────────────────

  /** @private */
  _buildPanel() {
    this._panel = createPanel({
      title: 'TUTORIAL', width: 0.62, height: 0.3, align: 'left',
    });
    this._panel.position.set(0, -0.16, -0.95);
    this._panel.visible = false;
    this.camera.add(this._panel);
  }

  /** @private */
  _buildDom() {
    if (typeof document === 'undefined') return;
    const host = document.getElementById(DOM_ID);
    if (!host) return;

    this._dom = {
      host,
      title: host.querySelector('.tut-title'),
      body: host.querySelector('.tut-body'),
      hint: host.querySelector('.tut-hint'),
      dots: host.querySelector('.tut-dots'),
      skip: host.querySelector('.tut-skip'),
      menu: document.getElementById('cc-tutorial-menu'),
      menuResume: document.querySelector('#cc-tutorial-menu .tut-resume'),
      menuSkip: document.querySelector('#cc-tutorial-menu .tut-menu-skip'),
    };

    this._dom.skip?.addEventListener('click', () => this.skip());
    this._dom.menuSkip?.addEventListener('click', () => { this._toggleMenu(false); this.skip(); });
    this._dom.menuResume?.addEventListener('click', () => this._toggleMenu(false));
  }

  /** @param {import('./steps.js').TutorialStep} step @private */
  _render(step) {
    const d = this._dom;
    if (d.host) {
      d.host.classList.add('on');
      if (d.title) d.title.textContent = step.title;
      if (d.body) d.body.textContent = step.body;
      if (d.hint) d.hint.innerHTML = step.hint || '';
      if (d.dots) {
        d.dots.innerHTML = TUTORIAL_STEPS
          .map((_, i) => `<i class="${i <= this.index ? 'done' : ''}"></i>`).join('');
      }
    }
    if (this._panel) {
      this._panel.setTitle(`${this.index + 1}/${TUTORIAL_STEPS.length}  ${step.title}`);
      this._panel.setLines([step.body]);
      this._panel.visible = this._inVR;
    }
  }

  /** @private */
  _hideDom() {
    this._dom.host?.classList.remove('on');
    this._dom.menu?.classList.remove('on');
  }

  /** @param {boolean} on @private */
  _setInStall(on) {
    this._stallActive = on;
    this._dom.host?.classList.toggle('in-stall', on);
  }

  /** @param {boolean} [force] @private */
  _toggleMenu(force) {
    this._menuOpen = force === undefined ? !this._menuOpen : force;
    this._dom.menu?.classList.toggle('on', this._menuOpen);
  }

  /** @param {boolean} on @private */
  _setVR(on) {
    this._inVR = on;
    if (this._dom.host) this._dom.host.classList.toggle('vr', on);
    if (this._panel) this._panel.visible = on && this.active;
  }
}

/** The one tutorial. */
export const tutorial = new Tutorial();

/**
 * Whether onboarding should run this session. Kept here rather than in main.js
 * so the rule (fresh save, not explicitly skipped) lives with the feature.
 * @returns {boolean}
 */
export function shouldRunTutorial() {
  return !options.skipTutorial && !save.data?.tutorial?.completed;
}
